import type { ApiCaller } from '../api/api-caller';
import type { ActionCallDto, MessageDto } from '../types/dtos';
import {
  type WidgetMessageReplyTo,
  type WidgetMessageU,
  type WidgetSystemMessageU,
} from '../types/messages';
import type { WidgetConfig } from '../types/widget-config';
import { isExhaustive } from '../utils/is-exhaustive';
import { Poller } from '../utils/Poller';
import { runCatching } from '../utils/run-catching';
import type { MessageCtx } from './message.ctx';
import type { SessionCtx } from './session.ctx';
import { log } from '../utils/log';

export class ActiveSessionPollingCtx {
  private api: ApiCaller;
  private config: WidgetConfig;
  private sessionCtx: SessionCtx;
  private messageCtx: MessageCtx;
  private sessionPollingIntervalSeconds: number;

  private poller = new Poller();
  private fetchSessionAndFullHistoryAbortController = new AbortController();
  private reconcileAbortController = new AbortController();
  private subscriptions: Array<() => void> = [];
  /** Session the in-flight post-stream reconcile belongs to, if any. */
  private reconcilingSessionId: string | null = null;

  constructor({
    api,
    config,
    sessionCtx,
    messageCtx,
    sessionPollingIntervalSeconds,
  }: {
    api: ApiCaller;
    config: WidgetConfig;
    sessionCtx: SessionCtx;
    messageCtx: MessageCtx;
    sessionPollingIntervalSeconds: number;
  }) {
    this.api = api;
    this.config = config;
    this.sessionCtx = sessionCtx;
    this.messageCtx = messageCtx;
    this.sessionPollingIntervalSeconds = sessionPollingIntervalSeconds;

    this.registerPolling();
  }

  private registerPolling = () => {
    this.subscriptions.push(
      this.sessionCtx.sessionState.subscribe(({ session }) => {
        if (session?.id) {
          this.poller.startPolling(async (abortSignal) => {
            this.fetchSessionAndHistory({
              sessionId: session.id,
              abortSignal,
            });
          }, this.sessionPollingIntervalSeconds * 1000);
        } else {
          this.poller.reset();
        }
      }),
    );

    /**
     * When the session is closed, fetch immediately instead of waiting for the
     * next poll tick, so closing messages (e.g. csat_requested) show up right away.
     */
    this.subscriptions.push(
      this.sessionCtx.sessionState.subscribe(({ session }) => {
        if (session?.id && !session.isOpened) {
          try {
            this.fetchSessionAndFullHistoryAbortController =
              new AbortController();
            this.fetchSessionAndHistory({
              sessionId: session.id,
              abortSignal:
                this.fetchSessionAndFullHistoryAbortController.signal,
            });
          } catch (error) {
            if (
              !this.fetchSessionAndFullHistoryAbortController.signal.aborted
            ) {
              log.error('failed to fetch session and full history', error);
            }
          }
        } else {
          this.fetchSessionAndFullHistoryAbortController.abort();
        }
      }),
    );

    /**
     * A post-stream reconcile still in flight when the active session stops
     * being the one it was started for (new chat, session switch) must not
     * land: its rows belong to the old conversation and would be appended to
     * the fresh transcript.
     */
    this.subscriptions.push(
      this.sessionCtx.sessionState.subscribe(({ session }) => {
        if (this.reconcilingSessionId === null) return;
        if (session?.id === this.reconcilingSessionId) return;
        this.reconcileAbortController.abort();
      }),
    );
  };

  dispose = () => {
    this.poller.reset();
    this.fetchSessionAndFullHistoryAbortController.abort();
    this.reconcileAbortController.abort();
    this.reconcilingSessionId = null;
    this.subscriptions.forEach((unsubscribe) => unsubscribe());
    this.subscriptions = [];
  };

  /**
   * Ingest the canonical rows the backend persisted for a just-settled
   * streamed turn. Owns a REAL abort controller — cancelled on the session
   * boundary (see `registerPolling`), which is the whole point: the signal
   * this passes must be one somebody can actually fire.
   */
  reconcileAfterStream = async (sessionId: string): Promise<void> => {
    this.reconcileAbortController.abort();
    this.reconcileAbortController = new AbortController();
    const { signal } = this.reconcileAbortController;
    this.reconcilingSessionId = sessionId;
    try {
      await this.fetchSessionAndHistory({ sessionId, abortSignal: signal });
    } catch (error) {
      // An abort is this method doing its job, not a failure.
      if (!signal.aborted) throw error;
    } finally {
      if (this.reconcilingSessionId === sessionId) {
        this.reconcilingSessionId = null;
      }
    }
  };

  fetchSessionAndHistory = async ({
    sessionId,
    abortSignal,
  }: {
    sessionId: string;
    abortSignal: AbortSignal;
  }): Promise<void> => {
    /**
     * This is a bit of an implicit contract... there are two cases here
     * 1. If there are no messages in state, it means the user selected a previous session from the sessions screen and got routed to the chat,
     *    in this case, we want to show a loading indicator until the initial fetch is done
     * 2. There is a single message in state, which is the optimistically rendered user message,
     *    in this case, we don't want to show a loading indicator
     */
    const isInitialFetch = this.messageCtx.state.get().messages.length === 0;
    if (isInitialFetch) {
      this.messageCtx.state.setPartial({ isInitialFetchLoading: true });
    }

    const { data } = await this.api.pollSessionAndHistory({
      sessionId,
      abortSignal,
    });

    if (data?.session) {
      this.sessionCtx.sessionState.setPartial({ session: data.session });
      this.sessionCtx.setSessions([data.session]);
    }

    if (data?.history && data.history.length > 0) {
      this.reconcileOptionPicks(data.history);
      // Get a fresh reference to current messages after the poll is done
      const prevMessages = this.messageCtx.state.get().messages;
      const newMessages = data.history
        .map(this.mapHistoryToMessage)
        .filter((msg): msg is WidgetMessageU => msg !== null)
        .filter(
          (newMsg) =>
            !prevMessages.some((existingMsg) => existingMsg.id === newMsg.id),
        );
      // Only commit when the poll actually surfaced NEW rows. A poll that
      // returns only already-known messages must NOT replace the array with a
      // fresh reference — that re-renders the whole transcript on every tick
      // (visible flicker during a stream, and it remounts the lazy recharts
      // chart into an empty 0-measured ResponsiveContainer). The steady state
      // of an idle/streaming session is "poll returns the same rows", so this
      // guard is what keeps the surface still between real updates.
      if (newMessages.length > 0) {
        this.messageCtx.state.setPartial({
          messages: [...prevMessages, ...newMessages],
        });
        if (isInitialFetch) {
          // Opening an existing session: history flows in as one batch. The user
          // is loading context, not receiving new messages — suppress the hook
          // but seed the dedup set so later polls don't re-fire these ids.
          this.messageCtx.markAsDispatchedToOnMessageReceivedHook(
            newMessages.map((m) => m.id),
          );
        } else {
          for (const newMessage of newMessages) {
            this.messageCtx.dispatchToOnMessageReceivedHook(newMessage);
          }
        }
      }
    }

    if (this.messageCtx.state.get().isInitialFetchLoading) {
      this.messageCtx.state.setPartial({ isInitialFetchLoading: false });
    }
  };

  /** A pick recorded elsewhere (another tab) lands on the message this tab already shows. */
  private reconcileOptionPicks = (history: MessageDto[]): void => {
    for (const row of history) {
      const pickedOptionId = row.messageOptions?.pickedOptionId;
      if (pickedOptionId) {
        this.messageCtx.setMessageOptionPick(row.publicId, pickedOptionId);
      }
    }
  };

  mapHistoryToMessage = (history: MessageDto): WidgetMessageU | null => {
    const commonFields = {
      id: history.publicId,
      timestamp: history.sentAt || '',
      attachments: history.attachments || undefined,
    };

    if (history.sender.kind === 'user') {
      return {
        ...commonFields,
        type: 'USER',
        deliveredAt: commonFields.timestamp,
        content: history.content.text || '',
        // Backend field name (schema.ts) → widget vocabulary.
        markedElements: history.pickedElements,
      };
    }

    if (history.sender.kind === 'agent') {
      return {
        ...commonFields,
        type: 'AGENT',
        component: 'agent_message',
        data: {
          message: history.content.text || '',
        },
        agent: {
          name: history.sender.name || '',
          // Do not set avatarUrl from config here... let it be taken from the config at render time
          // Only set avatar url from the backend's response
          avatarUrl: history.sender.avatar || null,
          avatar: history.sender.avatar || null,
          id: null,
          isAi: false,
        },
        ...this.constructReplyTo(history),
        ...(history.messageOptions
          ? { messageOptions: history.messageOptions }
          : {}),
      };
    }

    if (history.sender.kind === 'ai') {
      const action =
        history.actionCalls && history.actionCalls.length > 0
          ? history.actionCalls[history.actionCalls.length - 1]
          : undefined;

      return {
        ...commonFields,
        type: 'AI',
        component: 'bot_message',
        agent: {
          id: null,
          name: this.config.bot?.name || '',
          isAi: true,
          // Do not set avatarUrl from config here... let it be taken from the config at render time
          avatarUrl: null,
          avatar: null,
        },
        data: {
          message: history.content.text || '',
          action: action
            ? {
                name: action.actionName,
                data: this.extractActionResult(action),
              }
            : undefined,
        },
        ...this.constructReplyTo(history),
      };
    }

    if (history.sender.kind === 'system') {
      const message = this.constructSystemMessage(history);
      if (message === null) return null;

      return { ...message };
    }

    return null;
  };

  /**
   * The quoted message carried by a reply — a teammate answering a specific
   * message, or the AI following up on one after asking the team. Spread into
   * the message so an absent quote adds no key at all.
   */
  constructReplyTo = (
    history: MessageDto,
  ): { replyTo?: WidgetMessageReplyTo } => {
    if (!history.replyTo) return {};
    const { publicId, text, sender } = history.replyTo;
    return {
      replyTo: {
        id: publicId,
        text,
        sender,
      },
    };
  };

  constructSystemMessage = (
    history: MessageDto,
  ): WidgetSystemMessageU | null => {
    if (!history || !history.systemMessagePayload) return null;
    switch (history.systemMessagePayload.type) {
      case 'state_checkpoint':
        return {
          id: history.publicId,
          type: 'SYSTEM',
          subtype: 'state_checkpoint',
          data: { payload: history.systemMessagePayload.payload },
          timestamp: history.sentAt || '',
          attachments: undefined,
        };
      case 'csat_requested':
        return {
          id: history.publicId,
          type: 'SYSTEM',
          subtype: 'csat_requested',
          data: { payload: undefined },
          timestamp: history.sentAt || '',
          attachments: undefined,
        };
      case 'csat_submitted':
        return {
          id: history.publicId,
          type: 'SYSTEM',
          subtype: 'csat_submitted',
          data: {
            payload: {
              score: history.systemMessagePayload.payload.score ?? undefined,
              feedback:
                history.systemMessagePayload.payload.feedback ?? undefined,
            },
          },
          timestamp: history.sentAt || '',
          attachments: undefined,
        };
      case 'none':
        return null;
      default:
        isExhaustive(
          history.systemMessagePayload,
          this.constructSystemMessage.name,
        );
        return null;
    }
  };

  extractActionResult = (action: ActionCallDto) => {
    const result = action.result;

    if (result === null) return result;
    if (typeof result !== 'object') return result;

    if (
      'responseBodyText' in result &&
      typeof result.responseBodyText === 'string'
    ) {
      const responseBodyText = result.responseBodyText;
      const parsed = runCatching(() => JSON.parse(responseBodyText)).data;
      if (parsed) return parsed;
    }

    return action.result;
  };
}
